import React, { useState, useEffect } from "react";
import { toast } from "react-toastify";
import { FaTicketAlt, FaTimes, FaCheck } from "react-icons/fa";
import axios from "axios";
import { API_URL } from "../config/apiConfig";

const CouponApplicator = ({
  bookingAmount = 0,
  cabType = "standard",
  onCouponApplied = () => {},
  onCouponRemoved = () => {},
}) => {
  const [couponCode, setCouponCode] = useState("");
  const [appliedCoupon, setAppliedCoupon] = useState(null);
  const [availableCoupons, setAvailableCoupons] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [showAvailable, setShowAvailable] = useState(false);



  // Get auth token
  const getAuthToken = () => {
    const user = JSON.parse(localStorage.getItem("user") || "{}");
    return user?.token;
  };

  // Fetch available coupons
  const fetchAvailableCoupons = async () => {
    try {
      const token = getAuthToken();
      if (!token) {
        return;
      }

      const response = await axios.get(
        `${API_URL}/coupons/available`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        },
      );

      if (response.data.status) {
        setAvailableCoupons(response.data.coupons || []);
      } else {
      }
    } catch (error) {
      console.error("Error fetching available coupons:", error);
      // Try to fetch from public endpoint as fallback
      try {
        const publicResponse = await axios.get(`${API_URL}/coupons/active`);
        if (publicResponse.data.status) {
          setAvailableCoupons(publicResponse.data.coupons || []);
        }
      } catch (publicError) {
        console.error("Error fetching from public endpoint:", publicError);
      }
    }
  };

  // Load available coupons on component mount
  useEffect(() => {
    fetchAvailableCoupons();
  }, []);

  // Validate coupon
  const validateCoupon = async (code) => {
    try {
      setIsLoading(true);
      const token = getAuthToken();

      if (!token) {
        toast.error("Please log in to apply coupons");
        return null;
      }

      const response = await axios.post(
        `${API_URL}/coupons/validate`,
        {
          code: code.toUpperCase(),
          bookingAmount,
          cabType,
        },
        {
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
        },
      );

      if (response.data.status) {
        return response.data.coupon;
      } else {
        toast.error(response.data.message || "Invalid coupon");
        return null;
      }
    } catch (error) {
      const errorMessage =
        error.response?.data?.message || "Failed to validate coupon";
      toast.error(errorMessage);
      console.error("Coupon validation error:", error);
      return null;
    } finally {
      setIsLoading(false);
    }
  };

  // Apply coupon
  const applyCoupon = async () => {
    if (!couponCode.trim()) {
      toast.error("Please enter a coupon code");
      return;
    }

    if (bookingAmount <= 0) {
      toast.error("Please select a valid booking before applying coupon");
      return;
    }

    const validatedCoupon = await validateCoupon(couponCode.trim());

    if (validatedCoupon) {
      setAppliedCoupon(validatedCoupon);
      setCouponCode("");
      setShowAvailable(false);
      toast.success(`Coupon ${validatedCoupon.code} applied successfully!`);

      // Call parent callback
      onCouponApplied({
        code: validatedCoupon.code,
        discountAmount: validatedCoupon.discountAmount,
        originalAmount: validatedCoupon.originalAmount,
        finalAmount: validatedCoupon.finalAmount,
      });
    }
  };

  // Remove applied coupon
  const removeCoupon = () => {
    setAppliedCoupon(null);
    setCouponCode("");
    toast.info("Coupon removed");

    // Call parent callback
    onCouponRemoved();
  };

  // Apply a specific coupon from available list
  const applySpecificCoupon = async (code) => {
    setCouponCode(code);
    const validatedCoupon = await validateCoupon(code);

    if (validatedCoupon) {
      setAppliedCoupon(validatedCoupon);
      setCouponCode("");
      setShowAvailable(false);
      toast.success(`Coupon ${validatedCoupon.code} applied successfully!`);

      // Call parent callback
      onCouponApplied({
        code: validatedCoupon.code,
        discountAmount: validatedCoupon.discountAmount,
        originalAmount: validatedCoupon.originalAmount,
        finalAmount: validatedCoupon.finalAmount,
      });
    }
  };

  return (
    <div className="coupon-applicator bg-gradient-to-r from-amber-50 to-orange-50 border border-amber-200 rounded-lg p-4 mb-4">
      {/* Header */}
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center">
          <FaTicketAlt className="text-amber-600 mr-2" />
          <h3 className="text-lg font-semibold text-gray-800">
            Apply Promo Code
          </h3>
        </div>
        {availableCoupons.length > 0 && (
          <button
            type="button"
            onClick={() => setShowAvailable(!showAvailable)}
            className="text-sm text-amber-600 hover:text-amber-700 underline"
          >
            {showAvailable ? "Hide" : "View"} Available Coupons
          </button>
        )}
      </div>

      {/* Applied Coupon Display */}
      {appliedCoupon && (
        <div className="applied-coupon bg-green-50 border border-green-200 rounded-lg p-3 mb-3">
          <div className="flex items-center justify-between">
            <div className="flex items-center">
              <FaCheck className="text-green-600 mr-2" />
              <div>
                <span className="font-semibold text-green-800">
                  {appliedCoupon.code} Applied
                </span>
                <p className="text-sm text-green-600">
                  {appliedCoupon.description || "Discount applied successfully"}
                </p>
              </div>
            </div>
            <div className="flex items-center">
              <span className="text-green-700 font-bold mr-2">
                -₹{appliedCoupon.discountAmount?.toFixed(2) || "0.00"}
              </span>
              <button
                type="button"
                onClick={removeCoupon}
                className="text-red-500 hover:text-red-600 p-1"
                title="Remove coupon"
              >
                <FaTimes />
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Coupon Input */}
      {!appliedCoupon && (
        <div className="coupon-input-section">
          <div className="flex space-x-2 mb-3">
            <input
              type="text"
              value={couponCode}
              onChange={(e) => setCouponCode(e.target.value.toUpperCase())}
              placeholder="Enter promo code"
              className="flex-1 border border-gray-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-amber-500 focus:border-transparent"
              disabled={isLoading}
              onKeyPress={(e) => {
                if (e.key === "Enter") {
                  e.preventDefault();
                  applyCoupon();
                }
              }}
            />
            <button
              type="button"
              onClick={applyCoupon}
              disabled={isLoading || !couponCode.trim()}
              className="apply-button bg-amber-600 text-white px-4 py-2 rounded-md text-sm font-medium hover:bg-amber-700 disabled:bg-amber-400 disabled:cursor-not-allowed transition-colors"
            >
              {isLoading ? "Applying..." : "Apply"}
            </button>
          </div>

          {/* Available Coupons */}
          {showAvailable && availableCoupons.length > 0 && (
            <div className="available-coupons">
              <h4 className="text-sm font-medium text-gray-700 mb-2">
                Available promo codes ({availableCoupons.length}):
              </h4>
              <div className="flex flex-wrap gap-2">
                {availableCoupons.map((coupon) => (
                  <button
                    key={coupon._id}
                    type="button"
                    onClick={() => applySpecificCoupon(coupon.code)}
                    disabled={isLoading}
                    className="coupon-chip bg-white border border-amber-300 text-amber-700 px-3 py-1 rounded-full text-xs font-medium hover:bg-amber-50 hover:border-amber-400 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                    title={coupon.description}
                  >
                    {coupon.code}
                    {coupon.discountType === "percentage"
                      ? ` (${coupon.discountValue}% off)`
                      : ` (₹${coupon.discountValue} off)`}
                  </button>
                ))}
              </div>
            </div>
          )}

          {/* Sample Available Coupons (fallback if no API coupons) */}
          {!showAvailable && availableCoupons.length === 0 && (
            <div className="text-xs text-gray-600">
              <p className="mb-1">Available promo codes from admin:</p>
              <p>WELCOME10, FIRST20, SPECIAL15, SAVE50, LUXURY25</p>
              <p className="text-amber-600 mt-1">Click "View Available Coupons" to see more details</p>
            </div>
          )}
        </div>
      )}

      {/* Booking Amount Info */}
      {bookingAmount > 0 && (
        <div className="text-xs text-gray-500 mt-2">
          Booking amount: ₹{bookingAmount.toFixed(2)}
        </div>
      )}
    </div>
  );
};

export default CouponApplicator;
